const ENVIRONMENT = {
    DEVELOPMENT: "http://localhost:8000",
    TEST: "https://test-api.joinclockout.com",
    STAGING: "https://staging.joinclockout.com",
    TVFCU_TEST: "https://tvfcu-test.joinclockout.com",
    TVFCU_PROD: "https://tvfcu.joinclockout.com"
};

class UrlFactory {
    apiUrl = null;

    constructor() {
        this.apiUrl = process.env.REACT_APP_API_URL || ENVIRONMENT["DEVELOPMENT"];
    }

    createRegisterUrl() {
        return `${this.apiUrl}/api/user/register`;
    }

    createUserUrl() {
        return `${this.apiUrl}/api/user`;
    }

    createAuthenticateUrl() {
        return `${this.apiUrl}/api/user/authenticate`;
    }

    createReportUrl(aba, start, end, grouping, timeFrame) {
        if (start && end) {
            const params = new URLSearchParams();
            params.set("start", start);
            params.set("end", end);
            if (grouping) params.set("grouping", grouping);
            if (timeFrame) params.set("timeFrame", timeFrame);
            return `${this.apiUrl}/api/report/${aba}?${params.toString()}`;
        }
        return `${this.apiUrl}/api/report/${aba}`;
    }

    createFIUrl(aba) {
        return `${this.apiUrl}/api/consumer/fi/${aba}`;
    }

    // The single scope-gated, audited call that returns the whole Open SSO boot
    // bundle (apiKey + identity + flags + a server-minted sessionId).
    createSsoSessionUrl(aba, clientId) {
        return `${this.apiUrl}/api/admin/users/${aba}/${clientId}/sso/session`;
    }

    // Lifecycle events for one SSO session: warning_shown / warning_cancelled /
    // session_closed.
    createSsoEventsUrl(aba, clientId) {
        return `${this.apiUrl}/api/admin/users/${aba}/${clientId}/sso/events`;
    }

    createUsersUrl(aba) {
        return `${this.apiUrl}/api/users/fi/${aba}`;
    }

    createAdminUsersUrl(aba) {
        return `${this.apiUrl}/api/admin/users/${aba}`;
    }

    createLedgerUrl(aba) {
        return `${this.apiUrl}/api/consumer/ledger/${aba}`;
    }

    createYTDReportUrl(aba) {
        return `${this.apiUrl}/api/report/ytd/${aba}`;
    }

    createConsumerUserUrl(clientId) {
        return `${this.apiUrl}/api/consumer/user/${clientId}`;
    }

    createReconciliationUrl(aba) {
        return `${this.apiUrl}/api/report/unsettled-advances?aba=${aba}`;
    }

    createTreasuryFundingUrl(aba) {
        return `${this.apiUrl}/api/treasury/funding/${aba}`;
    }

    createTreasuryKPIsUrl(aba) {
        return `${this.apiUrl}/api/treasury/kpis/${aba}`;
    }

    createTreasuryReceivablesUrl(aba) {
        return `${this.apiUrl}/api/treasury/receivables/${aba}`;
    }

    createTreasuryActivitiesUrl(aba) {
        return `${this.apiUrl}/api/treasury/activities/${aba}`;
    }

    createTreasuryCashflowUrl(aba) {
        return `${this.apiUrl}/api/treasury/cashflow/${aba}`;
    }

    createTreasuryAdvanceGLUrl(aba) {
        return `${this.apiUrl}/api/treasury/advance-gl/${aba}`;
    }

    createTreasuryFeeGLUrl(aba) {
        return `${this.apiUrl}/api/treasury/fee-gl/${aba}`;
    }

    createTreasuryTransactionsUrl(aba) {
        return `${this.apiUrl}/api/treasury/transactions/${aba}`;
    }

    createDashboardEnrollmentUrl(aba) {
        return `${this.apiUrl}/api/dashboard/enrollment/${aba}`;
    }

    createDashboardUserSummaryUrl(aba) {
        return `${this.apiUrl}/api/dashboard/enrollment/${aba}/user-summary`;
    }

    createDashboardCohortUsersUrl(aba) {
        return `${this.apiUrl}/api/dashboard/enrollment/${aba}/cohort-users`;
    }

    createDashboardFunnelUrl(aba) {
        return `${this.apiUrl}/api/dashboard/enrollment/${aba}/funnel`;
    }

    createDashboardUsageUrl(aba) {
        return `${this.apiUrl}/api/dashboard/usage/${aba}`;
    }

    createDashboardTransactionSummaryUrl(aba) {
        return `${this.apiUrl}/api/dashboard/usage/${aba}/transactions`;
    }

    createDashboardSessionEventsUrl(aba) {
        return `${this.apiUrl}/api/dashboard/usage/${aba}/session-events`;
    }

    createDashboardMonetizationUrl(aba) {
        return `${this.apiUrl}/api/dashboard/monetization/${aba}`;
    }

    createEnrollmentFunnelUrl(aba) {
        return `${this.apiUrl}/api/reports/enrollment-funnel/${aba}`;
    }

    // POST /api/admin/reports/:aba/export — server-side Reports export (KAN-548)
    createReportsExportUrl(aba) {
        return `${this.apiUrl}/api/admin/reports/${aba}/export`;
    }

    // GET /api/admin/reports/:aba/export/:job_id — poll an async export
    createReportsExportJobUrl(aba, jobId) {
        return `${this.apiUrl}/api/admin/reports/${aba}/export/${encodeURIComponent(jobId)}`;
    }

    createRemoveUserAccessUrl(aba, userId) {
        return `${this.apiUrl}/api/admin/users/${aba}/${userId}/remove-access`;
    }

    createChangeUserRoleUrl(aba, userId) {
        return `${this.apiUrl}/api/admin/users/${aba}/${userId}/change-role`;
    }

    createSendInvitationUrl() {
        return `${this.apiUrl}/api/admin/users/send-invitation`;
    }

    createValidateInvitationUrl(token) {
        return `${this.apiUrl}/api/user/validate-invitation?token=${token}`;
    }

    createUpdatePasswordUrl() {
        return `${this.apiUrl}/api/user/update-password`;
    }

    createValidatePasswordResetUrl(token) {
        return `${this.apiUrl}/api/user/validate-password-reset?token=${token}`;
    }

    createUpdateAccountUrl() {
        return `${this.apiUrl}/api/user/account`;
    }

    createSendPasswordResetUrl(aba) {
        return `${this.apiUrl}/api/admin/users/send-password-reset/${aba}`;
    }

    createRolesUrl(aba) {
        return `${this.apiUrl}/api/roles/${aba}`;
    }

    createRoleDetailUrl(aba, roleId) {
        return `${this.apiUrl}/api/roles/${aba}/${roleId}`;
    }

    createRoleScopesUrl(aba, roleId) {
        return `${this.apiUrl}/api/roles/${aba}/${roleId}/scopes`;
    }

    createRoleSetDefaultUrl(aba, roleId) {
        return `${this.apiUrl}/api/roles/${aba}/${roleId}/set-default`;
    }

    createScopesCatalogUrl(aba) {
        return `${this.apiUrl}/api/roles/scopes/catalog?aba=${aba}`;
    }

    createBillingKPIsUrl(aba) {
        return `${this.apiUrl}/api/billing/kpis/${aba}`;
    }

    createAnalyticsBillableUsersUrl(aba) {
        return `${this.apiUrl}/api/analytics/${aba}/billable-users`;
    }

    createAnalyticsMetricsUrl(aba) {
        return `${this.apiUrl}/api/analytics/${aba}/metrics`;
    }

    createReviewsSummaryUrl(aba) {
        return `${this.apiUrl}/api/reviews/${encodeURIComponent(aba)}/summary`;
    }

    createReviewsUrl(aba) {
        return `${this.apiUrl}/api/reviews/${encodeURIComponent(aba)}`;
    }

    createReviewDetailUrl(aba, eventId) {
        return `${this.apiUrl}/api/reviews/${encodeURIComponent(aba)}/${encodeURIComponent(eventId)}`;
    }

    createBillingStatementsUrl(aba) {
        return `${this.apiUrl}/api/billing/statements/${aba}`;
    }

    createExportBillingStatementsUrl(aba) {
        return `${this.apiUrl}/api/billing/export/${aba}`;
    }

    createSyncMonitorUrl(aba, status = null, limit = 50) {
        let url = `${this.apiUrl}/api/admin/sync-monitor/${aba}?limit=${limit}`;
        if (status) url += `&status=${status}`;
        return url;
    }

    createSyncNotifyUrl(aba) {
        return `${this.apiUrl}/api/admin/sync-monitor/${aba}/notify`;
    }

    createTroubleTicketResolveUrl(ticketId) {
        return `${this.apiUrl}/api/admin/trouble-ticket/${ticketId}/resolve`;
    }

    createPlatformsUrl(aba, provider = null, search = null) {
        let url = `${this.apiUrl}/api/admin/platforms/${aba}`;
        const params = [];
        if (provider) params.push(`provider=${provider}`);
        if (search) params.push(`search=${encodeURIComponent(search)}`);
        if (params.length > 0) url += `?${params.join("&")}`;
        return url;
    }

    createPlatformOutagesUrl(aba, platformId = null, provider = null, status = "active") {
        let url = `${this.apiUrl}/api/admin/platform-outages/${aba}`;
        const params = [];
        if (platformId) params.push(`platform_id=${platformId}`);
        if (provider) params.push(`provider=${provider}`);
        if (status) params.push(`status=${status}`);
        if (params.length > 0) url += `?${params.join("&")}`;
        return url;
    }

    createPlatformOutageCreateUrl(aba) {
        return `${this.apiUrl}/api/admin/platform-outage/${aba}`;
    }

    createPlatformOutageUpdateUrl(aba, outageId) {
        return `${this.apiUrl}/api/admin/platform-outage/${aba}/${outageId}`;
    }

    createPlatformOutageResolveUrl(aba, outageId) {
        return `${this.apiUrl}/api/admin/platform-outage/${aba}/${outageId}/resolve`;
    }

    createUserSyncStatusUrl(aba, clientId) {
        return `${this.apiUrl}/api/admin/users/${aba}/${clientId}/sync-status`;
    }

    createUserFunnelUrl(aba, clientId) {
        return `${this.apiUrl}/api/admin/users/${aba}/${clientId}/funnel`;
    }

    createUserLinkAttemptsUrl(aba, clientId) {
        return `${this.apiUrl}/api/admin/users/${aba}/${clientId}/link-attempts`;
    }

    // GET: /api/admin/reports/:aba/sync-state-catalog
    createSyncStateCatalogUrl(aba) {
        return `${this.apiUrl}/api/admin/reports/${aba}/sync-state-catalog`;
    }

    createUserPayrollConnectionsUrl(aba, clientId) {
        return `${this.apiUrl}/api/admin/users/${encodeURIComponent(aba)}/${encodeURIComponent(clientId)}/payroll-connections`;
    }

    // `connectionKey` is the summary payload's `connection_key`: either a uuid
    // `account_id`, or the literal "unassigned" for sync rows that never
    // received one (member opened Link and nothing came back).
    createUserPayrollConnectionEventsUrl(aba, clientId, connectionKey, query = {}) {
        const params = new URLSearchParams();
        if (query.page != null) params.set('page', String(query.page));
        if (query.perPage != null) params.set('perPage', String(query.perPage));
        if (query.status) params.set('status', query.status);
        if (query.kind) params.set('kind', query.kind);
        const q = params.toString();
        const base = `${this.apiUrl}/api/admin/users/${encodeURIComponent(aba)}/${encodeURIComponent(clientId)}/payroll-connections/${encodeURIComponent(connectionKey)}/events`;
        return q ? `${base}?${q}` : base;
    }

    createUserSyncHistoryUrl(aba, clientId) {
        return `${this.apiUrl}/api/admin/users/${encodeURIComponent(aba)}/${encodeURIComponent(clientId)}/sync-history`;
    }

    createUserRestrictUrl(aba, clientId) {
        return `${this.apiUrl}/api/admin/users/${aba}/${clientId}/restrict`;
    }

    createUserDeleteLatestSyncUrl(aba, clientId) {
        return `${this.apiUrl}/api/admin/users/${aba}/${clientId}/sync/latest`;
    }

    createUserMarkLatestSyncCompleteUrl(aba, clientId) {
        return `${this.apiUrl}/api/admin/users/${aba}/${clientId}/sync/latest/complete`;
    }

    // Not to be confused with createReconciliationUrl above — that one feeds
    // the FI-wide unsettled advances report; this settles one user's ledger.
    createUserReconcileAccountUrl(aba, clientId) {
        return `${this.apiUrl}/api/admin/users/${aba}/${clientId}/reconcile`;
    }

    createTicketsUrl(aba, query = {}) {
        const params = new URLSearchParams();
        if (query.status) params.set("status", query.status);
        if (query.page != null) params.set("page", String(query.page));
        if (query.perPage != null) params.set("perPage", String(query.perPage));
        if (query.sort) params.set("sort", query.sort);
        const q = params.toString();
        return q ? `${this.apiUrl}/api/admin/tickets/${encodeURIComponent(aba)}?${q}` : `${this.apiUrl}/api/admin/tickets/${encodeURIComponent(aba)}`;
    }

    createTicketUrl(aba, ticketId, query = {}) {
        const params = new URLSearchParams();
        if (query.page != null) params.set("page", String(query.page));
        if (query.perPage != null) params.set("perPage", String(query.perPage));
        const q = params.toString();
        const base = `${this.apiUrl}/api/admin/tickets/${encodeURIComponent(aba)}/${ticketId}`;
        return q ? `${base}?${q}` : base;
    }

    createTicketCreateUrl(aba) {
        return `${this.apiUrl}/api/admin/tickets/${encodeURIComponent(aba)}`;
    }

    createTicketFromEventUrl(aba) {
        return `${this.apiUrl}/api/admin/tickets/${encodeURIComponent(aba)}/from-event`;
    }

    createTicketMessagesUrl(aba, ticketId) {
        return `${this.apiUrl}/api/admin/tickets/${encodeURIComponent(aba)}/${ticketId}/messages`;
    }

    createTicketReadUrl(aba, ticketId) {
        return `${this.apiUrl}/api/admin/tickets/${encodeURIComponent(aba)}/${ticketId}/read`;
    }

    createTicketPresignedUrl(aba) {
        return `${this.apiUrl}/api/admin/tickets/${encodeURIComponent(aba)}/presigned`;
    }

    // Issue-tracker mirror (KAN-627/KAN-628). The FI only ever READS: the
    // service mirrors FI-visible Jira issues into Postgres and serves them
    // aba-scoped, so no Jira host, credential or raw payload is ever reachable
    // from the browser.
    //
    // GET: /api/admin/jira/:aba/issues — query: status, page, perPage.
    // Server-paginated for both views. `status` is a board COLUMN, not just a
    // narrowing: the board issues one request per status and pages each column
    // on its own as it is scrolled.
    createJiraIssuesUrl(aba, query = {}) {
        const params = new URLSearchParams();
        if (query.status) params.set("status", query.status);
        if (query.page != null) params.set("page", String(query.page));
        if (query.perPage != null) params.set("perPage", String(query.perPage));
        const q = params.toString();
        const base = `${this.apiUrl}/api/admin/jira/${encodeURIComponent(aba)}/issues`;
        return q ? `${base}?${q}` : base;
    }

    // GET: /api/admin/jira/:aba/issues/:key — one issue in full. Exists so the
    // list above can stay slim: priority, resolution, the fine-grained stage and
    // the linked support tickets are read one issue at a time.
    createJiraIssueUrl(aba, key) {
        return `${this.apiUrl}/api/admin/jira/${encodeURIComponent(aba)}/issues/${encodeURIComponent(key)}`;
    }

    // GET: /api/admin/jira/status-map — the shared status/stage vocabulary.
    // Deliberately NOT aba-scoped: the mapping table is one contract for every
    // FI (and for clockout-staff), so there is no `aba` in the path.
    createJiraStatusMapUrl() {
        return `${this.apiUrl}/api/admin/jira/status-map`;
    }

    // Admin notification feed (KAN-509/510). Query: type, unread, start, end, page, perPage.
    createNotificationsUrl(aba, query = {}) {
        const params = new URLSearchParams();
        if (query.type) params.set("type", query.type);
        if (query.unread === true || query.unread === "true") params.set("unread", "true");
        if (query.start) params.set("start", query.start);
        if (query.end) params.set("end", query.end);
        if (query.page != null) params.set("page", String(query.page));
        if (query.perPage != null) params.set("perPage", String(query.perPage));
        const q = params.toString();
        const base = `${this.apiUrl}/api/admin/notifications/${encodeURIComponent(aba)}`;
        return q ? `${base}?${q}` : base;
    }

    createNotificationsUnreadCountUrl(aba) {
        return `${this.apiUrl}/api/admin/notifications/${encodeURIComponent(aba)}/unread-count`;
    }

    createNotificationsReadUrl(aba) {
        return `${this.apiUrl}/api/admin/notifications/${encodeURIComponent(aba)}/read`;
    }

    createAccessListUrl(aba, action = null) {
        let url = `${this.apiUrl}/api/consumer/fi/${encodeURIComponent(aba)}/access-list`;
        if (action) url += `?action=${action}`;
        return url;
    }

    createAccessListRuleUrl(aba, ruleId) {
        return `${this.apiUrl}/api/consumer/fi/${encodeURIComponent(aba)}/access-list/${ruleId}`;
    }

    createAccessListBulkUploadUrl(aba) {
        return `${this.apiUrl}/api/consumer/fi/${encodeURIComponent(aba)}/access-list/bulk-upload`;
    }

    createAccessListBulkStatusUrl(aba, batchId) {
        return `${this.apiUrl}/api/consumer/fi/${encodeURIComponent(aba)}/access-list/bulk-upload/${batchId}`;
    }

    createFreeAdvancePromosUrl(aba) {
        return `${this.apiUrl}/api/consumer/fi/${encodeURIComponent(aba)}/free-advance-promos`;
    }

    createFreeAdvancePromoUrl(aba, promoId) {
        return `${this.apiUrl}/api/consumer/fi/${encodeURIComponent(aba)}/free-advance-promos/${promoId}`;
    }

    createFreeAdvancePromoMembersUrl(aba, promoId) {
        return `${this.apiUrl}/api/consumer/fi/${encodeURIComponent(aba)}/free-advance-promos/${promoId}/members`;
    }

    createFreeAdvancePromoMembersBatchUrl(aba, promoId) {
        return `${this.apiUrl}/api/consumer/fi/${encodeURIComponent(aba)}/free-advance-promos/${promoId}/members/batch`;
    }

    createFreeAdvancePromoMembersBatchRemoveUrl(aba, promoId) {
        return `${this.apiUrl}/api/consumer/fi/${encodeURIComponent(aba)}/free-advance-promos/${promoId}/members/batch/remove`;
    }

    createFreeAdvancePromoMemberUrl(aba, promoId, memberId) {
        return `${this.apiUrl}/api/consumer/fi/${encodeURIComponent(aba)}/free-advance-promos/${promoId}/members/${encodeURIComponent(memberId)}`;
    }


    createIntakeSimulationUrl(aba) {
        return `${this.apiUrl}/api/admin/intake-simulation/${encodeURIComponent(aba)}`;
    }

    createIntakeSimulationRunUrl(aba, simulationId) {
        return `${this.apiUrl}/api/admin/intake-simulation/${encodeURIComponent(aba)}/runs/${encodeURIComponent(simulationId)}`;
    }

    createIntakeSimulationPresetUrl(aba, mode) {
        return `${this.apiUrl}/api/admin/intake-simulation/${encodeURIComponent(aba)}/presets/${encodeURIComponent(mode)}`;
    }

    createIntakeSimulationMemberSorUrl(aba, clientId) {
        return `${this.apiUrl}/api/admin/intake-simulation/${encodeURIComponent(aba)}/member-sor/${encodeURIComponent(clientId)}`;
    }

    createDocumentIntakeJobsUrl(aba, clientId) {
        return `${this.apiUrl}/api/admin/document-intake/${encodeURIComponent(aba)}/jobs/${encodeURIComponent(clientId)}`;
    }

    createDocumentIntakeContentUrl(aba, clientId, jobId) {
        return `${this.apiUrl}/api/admin/document-intake/${encodeURIComponent(aba)}/content/${encodeURIComponent(clientId)}/${encodeURIComponent(jobId)}`;
    }
}

export default UrlFactory;
